import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';

import { DeleteDialogService } from './../../../core/services/dialog-services/delete-dialog.service';
import { TagService } from './../../../core/services/http-services/tag.service';
import { UIService } from './../../../core/services/utils/ui.service';
import { MonitorsService } from '../../../core/services/http-services/monitors.service';

import { PopupMessagesEnum } from '../../../core/enums/toastr-messages/popup-messages.enum';
import { TagViewModel } from './../../../core/models/view-models/tag-models/tag-view.model';
import { MainLibraryFormComponent } from '../../main-library-form/main-library-form.component';
import { LibraryEditItem } from './../../../core/models/library-edit-item.model';
import { LibraryEditItemTypeEnum } from './../../../core/enums/library-edit-item-type.enum';
import { MonitorKey } from '../../../core/models/view-models/monitor-models/monitor-key.type';
import { MonitorTagDialogMessagesEnum } from '../../../core/enums/toastr-messages/monitor-tag-dialog-messages.enum';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { Monitor } from '../../../core/models/entities/monitor.type';

@Component({
  selector: 'app-tag-form',
  templateUrl: './tag-form.component.html',
  styleUrls: ['./tag-form.component.scss'],
})
export class TagFormComponent implements OnInit, OnDestroy {
  @Input() tag: TagViewModel;
  @ViewChild(MainLibraryFormComponent, { static: true }) libraryFormComponent: MainLibraryFormComponent;

  public editItem: LibraryEditItem;
  public libraryEditItemTypes = LibraryEditItemTypeEnum;
  public monitorTagDialogMessagesEnum = MonitorTagDialogMessagesEnum;
  public isLoading: boolean;

  private oldTag: TagViewModel;
  private monitorKeysToUnAssign: MonitorKey[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly uiService: UIService,
    private readonly confirmService: DeleteDialogService,
    private readonly tagService: TagService,
    private readonly toastr: ToastrService,
    public readonly monitorsService: MonitorsService
  ) { }

  ngOnInit(): void {
    this.oldTag = _.cloneDeep(this.tag);
    this.prepareEditItem();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit(): void {
    if (this.libraryFormComponent.form.invalid) {
      return;
    }

    const {
      Title,
      Description,
      IsShared,
      IsSharedWithAllUsers
    } = this.libraryFormComponent.form.value;

    const tagModel = new TagViewModel(
      Object.assign(this.tag, {
        Title,
        Description,
        Color: this.editItem.Color,
        UsersWhoHaveAccessIds: IsShared ? this.libraryFormComponent.UsersWhoHaveAccessIds : [],
        IsShared: IsShared && (this.libraryFormComponent.UsersWhoHaveAccessIds.length || IsSharedWithAllUsers) ? IsShared : false,
        IsSharedWithAllUsers: IsShared ? IsSharedWithAllUsers : false,
      })
    );

    if (this.tag.Id) {
      tagModel.Id = this.tag.Id;
      this.updateTag(tagModel);

      if (this.monitorKeysToUnAssign.length) {
        this.tagService.unAssignMonitorTagFromMonitor({
          MonitorTagId: this.tag.Id,
          MonitorKeys: this.monitorKeysToUnAssign
        }).subscribe();
      }
    } else {
      this.createTag(tagModel);
    }

    this.isLoading = true;
  }

  exit(): void {
    if (this.libraryFormComponent.form.dirty ||
      !_.isEqual(this.oldTag.AssignedMonitorKeys, this.tag.AssignedMonitorKeys)) {
      this.subscriptions.add(
        this.confirmService
          .show(PopupMessagesEnum.SkipUpdatePlaylist)
          .subscribe((res) => {
            if (res) {
              this.close();
            }
          })
      );
    } else {
      this.close();
    }
  }

  removeAssignedMonitor(monitor: Monitor): void {
    this.subscriptions.add(
      this.confirmService.show(MonitorTagDialogMessagesEnum.UnAssignMonitorTagFromMonitorTitle)
        .subscribe((isToDelete: boolean) => {
          if (isToDelete) {
            this.tag.AssignedMonitorKeys = this.tag.AssignedMonitorKeys.filter(monitorKey =>
              !(monitorKey.MonitorHandler === monitor.MonitorHandler &&
              monitorKey.MachinePhysicalAddress === monitor.MachinePhysicalAddress));

            this.monitorKeysToUnAssign.push(new MonitorKey(monitor.MonitorHandler, monitor.MachinePhysicalAddress));
          }
        }));
  }

  private prepareEditItem(): void {
    const neededEditItemFields: any = Object.assign(
      _.pick(this.tag, Object.keys(new LibraryEditItem()))
    );
    neededEditItemFields.UsersWhoHaveAccessIds = this.tag.SharedUsers.map(x => x.Id);
    this.editItem = new TagViewModel(
      this.tag.Id ? neededEditItemFields : {}
    );
  }

  private createTag(tagModel: TagViewModel): void {
    this.subscriptions.add(
      this.tagService.createTag(tagModel).subscribe(
        (createdTag: TagViewModel) => {
          this.close();
          this.toastr.success(
            `\'${tagModel.Title}\' ${ToastrMessageEnum.SuccessfullyCreatedTitle}`,
            MonitorTagDialogMessagesEnum.TagCreationTitle
          );
          const tagViewModel: TagViewModel[] = this.tagService.tagCollectionChanged.value;
          this.tagService.tagCollectionChanged.next([...(tagViewModel ? tagViewModel : []), createdTag]);
        },
        () => this.close()
      )
    );
  }

  private updateTag(tagModel: TagViewModel): void {
    this.subscriptions.add(
      this.tagService.updateTag(tagModel).subscribe(
        (updatedtag: TagViewModel) => {
          this.close();
          const tags = this.tagService.tagCollectionChanged.getValue();
          const tagToUpdate = tags.find(tag => tag.Id === tagModel.Id);

          if (tagToUpdate) {
            Object.assign(tagToUpdate, updatedtag);
            const tagViewModel = tags.map(tag => tag.Id === tagToUpdate.Id ? tagToUpdate: tag);
            this.tagService.tagCollectionChanged.next(tagViewModel);
          }
          this.toastr.success(
            `\'${tagModel.Title}\' ${ToastrMessageEnum.SuccessfullyUpdatedTitle}`,
            MonitorTagDialogMessagesEnum.TagUpdatingTitle
          );
        },
        () => this.close()
      )
    );
  }

  private close(): void {
    this.uiService.hideLibraryEditPanel();
  }
}
